import { Component, OnInit, computed, Signal, signal, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { combineLatest, filter, map, Observable, of, shareReplay, switchMap, take } from 'rxjs';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import * as uuid from 'uuid';
import { filterNil } from 'ngxtension/filter-nil';
import { injectParams } from 'ngxtension/inject-params';
import { InstructionsComponent, TypeaheadSearchComponent, TypeaheadSearchResult } from '@r1-compass-library/components';
// prettier-ignore
import {
    ComparatorMetadata,
    ComparatorValueType,
    ContentAssetRule,
    DataMartField,
    DataMartMergeField,
    DataMartPicklistValue,
    isDatamartField,
    lookupComparators,
    mapValidities,
    Play,
    PlayImporterCSVField,
    transformCSVToDataRecords,
    ContentAsset,
} from '@r1-compass-library/models';
import { SelectedContentAssetStore } from '../../store/selected-content-asset.store';
import { DataMartTablesStore } from '../../../template-criteria/store/data-mart-tables.store';
import { DataMartService, FileTransferService, PlayService, RoutingService } from '@r1-compass-library/services';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { AutocompleteChipBuffetComponent } from '@r1-compass-library/components/autocomplete-chip-buffet/autocomplete-chip-buffet.component';
import { allEventsSignal, formValueObservable } from '@r1-compass-library/utilities/form-events';

import {
    FormUnsavedChangesWarningEntity,
    noopFormSaveMutationResult,
    wasSaveSuccessful,
} from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { ContentEditorComponent } from '../../../content-editor/content-editor.component';
import { ContentLibraryUtils } from '../../content-library.util';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { ContentLibraryRuleForm } from '@r1-compass-library/models/content-library/content-library-rule-form.model';
import {
    ContentLibraryValidationService,
    ContentLibrarySectionFormMap,
} from '../../content-library-validation.service';
import { ContentLibraryValidationStateService } from '../../content-library-validation-state.service';
import {
    SyncValidationStateSection,
    SyncValidationStateService,
    ValidationService,
    ValidationStateService,
    ContentLibrarySectionName,
} from '@r1-compass-library/features/validation-sync';
import { AssetInvalidSaveWarningService } from '../../../asset-invalid-save-warning.service';
import { FroalaImagesService } from '@r1-compass-library/services/froala/froala-images.service';
import { FieldMergeConstants } from '@r1-compass-library/constants/field-merge.constants';

@Component({
    selector: 'r1-compass-app-edit-rule',
    // prettier-ignore
    imports: [
        AutocompleteChipBuffetComponent,
        CommonModule,
        FontAwesomeModule,
        InstructionsComponent,
        ContentEditorComponent,
        MatCheckboxModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatNativeDateModule,
        MatSelectModule,
        ReactiveFormsModule,
        TypeaheadSearchComponent,
    ],
    templateUrl: './edit-rule.component.html',
    styleUrl: './edit-rule.component.scss',
})
export class EditRuleComponent
    implements
        OnInit,
        FormUnsavedChangesWarningEntity,
        SyncValidationStateSection<ContentLibrarySectionName, ContentAsset>
{
    readonly #router = inject(Router);
    readonly #contentAssetStore = inject(SelectedContentAssetStore);
    readonly #dataMartTablesStore = inject(DataMartTablesStore);
    readonly #dataMartService = inject(DataMartService);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #playService = inject(PlayService);
    readonly #route = inject(ActivatedRoute);
    readonly #froalaImagesService = inject(FroalaImagesService);
    readonly #fileTransferService = inject(FileTransferService);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #validationService = inject(ContentLibraryValidationService);
    readonly #validationStateService = inject(ContentLibraryValidationStateService);
    readonly #syncValidationStateService = inject(SyncValidationStateService);
    readonly #invalidSaveWarningService = inject(AssetInvalidSaveWarningService);

    readonly assetId = inject(RoutingService).assetId;

    contentLibraryRuleForm: FormGroup<ContentLibraryRuleForm> = new FormGroup<ContentLibraryRuleForm>(
        new ContentLibraryRuleForm()
    );
    assetRuleFormState = allEventsSignal<ReturnType<typeof this.contentLibraryRuleForm.getRawValue>>(
        this.contentLibraryRuleForm
    );
    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;

    private selectedIamDivision$ = toObservable(this.#globalLoggedInUserStore.selectedIamDivision);

    protected faCalendar = faCalendar;

    readonly canDeactivate$ = this.#formSaveWarningStore.deriveCanDeactivate$(this.contentLibraryRuleForm);
    readonly saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningMisc;

    readonly allowPartialSave = true;

    private readonly canDeactivate = toSignal(this.canDeactivate$, { requireSync: true });

    public disableSave = computed<boolean>(() =>
        this.#validationStateService.shouldDisableSave(
            this.#contentAssetStore.asset(),
            this.canDeactivate(),
            this.#validationStateService.invalidSections(),
            'rules'
        )
    );

    fields: Signal<DataMartField[]> = computed(
        () =>
            this.#dataMartTablesStore.fields()?.sort(function (a, b) {
                return a.displayName.localeCompare(b.displayName);
            }) ?? []
    );
    comparators: Signal<ComparatorMetadata[]> = computed(() => {
        const selectedField =
            this.type() === 'datamart'
                ? this.fields()?.find(field => field.name === this.assetRuleFormState().value.fieldName)
                : ({
                      name: this.assetRuleFormState().value.name,
                      dataType: this.assetRuleFormState().value.dataType,
                  } as PlayImporterCSVField);
        return selectedField
            ? lookupComparators(selectedField).filter(
                  comparator => !['date&date', 'timeInterval', 'text&text'].includes(comparator.valueType)
              )
            : [];
    });

    protected showNotCheckbox: Signal<boolean> = computed(
        () => !this.nonNegatableComparators.includes(this.selectedComparator()?.value ?? '-1')
    );
    protected showValueRow: Signal<boolean> = computed(
        () => !(this.selectedComparator()?.value === 'blank') && !(this.selectedComparator()?.value === 'isNull')
    );

    protected selectedComparatorValueType: Signal<ComparatorValueType> = computed(
        () => this.selectedComparator()?.valueType ?? 'nothing'
    );
    picklistOptions: Signal<DataMartPicklistValue[]> = toSignal(
        combineLatest(
            this.#dataMartTablesStore.table$,
            toObservable(this.fields),
            formValueObservable(this.contentLibraryRuleForm.controls.fieldName)
        ).pipe(
            map(([table, fields, fieldName]) => [table, fields?.find(field => field.name === fieldName)]),
            filter(([table, matchingField, _]) => !!table && !!matchingField),
            switchMap(([table, matchingField, _]) => {
                if (['multipicklist', 'picklist'].includes((matchingField as DataMartField).fieldType.toLowerCase())) {
                    return this.#dataMartService.getPicklist(table!.name, matchingField!.name);
                } else {
                    return of();
                }
            }),
            map(res => res?.entries ?? [])
        ),
        { initialValue: [] }
    );

    multiPickFormControlEntities: Signal<DataMartPicklistValue[]> = computed(() => {
        const fieldName = this.assetRuleFormState().value.fieldName;
        if (fieldName) {
            const matchingField = this.fields()?.find(field => field.name === fieldName);
            if (matchingField?.fieldType.toLowerCase() === 'multipicklist') {
                return this.picklistOptions().filter(opt =>
                    ContentLibraryUtils.convertRuleValueToStringArray(
                        this.contentLibraryRuleForm.controls.value.value
                    ).includes(opt.value)
                );
            } else {
                return [];
            }
        } else {
            return [];
        }
    });

    protected play$: Observable<Play | undefined> = this.#contentAssetStore.asset$.pipe(
        filterNil(),
        filter(asset => !!asset.playImporterId),
        switchMap(asset => this.#playService.get(asset.playImporterId!).pipe(take(1))),
        shareReplay(1) // prevents excess calls when the async pipe and the csvInfo$ uses it
    );

    play = toSignal(this.play$, { initialValue: undefined });

    type = toSignal(
        this.play$.pipe(
            map(play => play?.config?.playFileImport),
            map(imp => (imp ? 'csv' : 'datamart'))
        ),
        { initialValue: 'datamart' }
    );

    private csvInfo = toSignal(
        this.play$.pipe(
            switchMap(play =>
                // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                this.#fileTransferService.getFilePreview(play?.config?.playFileImport?.fileName!, String(play!.id))
            )
        ),
        { initialValue: [] }
    );

    validityMappings = computed(() => {
        const data: string[][] = this.csvInfo();

        return (
            // TODO - move where these are defined to lib lol
            mapValidities(transformCSVToDataRecords(data), ['integer', 'varchar', 'numeric', 'date', 'timestamptz']) ??
            []
        );
    });

    protected valueTypeaheadSearch = signal('');
    protected valueTypeaheadResults$: Observable<TypeaheadSearchResult[]> = combineLatest([
        toObservable(this.valueTypeaheadSearch),
        this.#dataMartTablesStore.table$,
        formValueObservable(this.contentLibraryRuleForm.controls.fieldName),
    ]).pipe(
        filter(([_, table, field]) => !!table?.name && !!field),
        switchMap(([typeahead, table, field]) => this.#dataMartService.typeaheadSearch(table!.name, field!, typeahead)),
        map(results =>
            results.entries.map(entry => {
                return {
                    display: entry,
                    value: entry,
                };
            })
        )
    );

    private nonNegatableComparators = ['lessThan', 'lessThanOrEqualTo', 'greaterThan', 'greaterThanOrEqualTo'];
    private selectedComparator: Signal<ComparatorMetadata | undefined> = computed(() =>
        this.comparators().find(item => item.value === this.assetRuleFormState().value.comparator)
    );
    readonly #ruleId: Signal<string | null> = injectParams('ruleId');
    private rule: Signal<ContentAssetRule | undefined> = computed(() => {
        return this.#contentAssetStore.asset()?.rules?.find(rule => String(rule.id) === this.#ruleId());
    });

    constructor() {
        const library = inject(FaIconLibrary);

        library.addIcons(faCalendar);

        effect(() => {
            if (this.type() === 'csv') {
                this.contentLibraryRuleForm.controls.dataType.addValidators(Validators.required);
            }
        });

        this.saveGuardHandlerFn({
            canDeactivate$: this.canDeactivate$,
            disableSave: this.disableSave,
        });
    }

    ngOnInit() {
        this.selectedIamDivision$.pipe(filterNil()).subscribe(selectedIamDivision => {
            const tableName = this.#globalLoggedInUserStore.selectedDivisionConfig()?.config?.mapping?.dataMartTable;
            if (tableName) {
                this.#dataMartTablesStore.getTable({ tableName: tableName });
            }
        });
        const assetId = this.#route.snapshot.paramMap.get('assetId');
        if (assetId && assetId !== 'new' && this.#contentAssetStore.asset()?.id !== +assetId) {
            this.#contentAssetStore.getAsset({ id: +assetId });
        }
        this.#contentAssetStore.asset$.pipe(filterNil(), take(1)).subscribe(asset => {
            const rule = this.rule();
            if (asset.playImporterId && rule?.field?.name) {
                rule.field.name = rule.field.name.trim(); // Always trim any value that is derived from a import file header
            }

            const rawTemplate = rule?.talkingPointsTemplate ?? '';
            this.#froalaImagesService
                .hydrateImagePlaceholders(rawTemplate, String(asset.id), 'content')
                .pipe(take(1))
                .subscribe(hydratedHtml => {
                    const formValue = new FormGroup<ContentLibraryRuleForm>(
                        new ContentLibraryRuleForm(rule)
                    ).getRawValue();

                    this.contentLibraryRuleForm.patchValue({
                        ...formValue,
                        talkingPointsTemplate: hydratedHtml,
                    });

                    if (
                        this.contentLibraryRuleForm.controls.comparator.value === 'blank' ||
                        this.contentLibraryRuleForm.controls.comparator.value === 'isNull'
                    ) {
                        this.contentLibraryRuleForm.controls.value.disable();
                    }

                    // For existing configs with a play plus Datamart that need to be wiped to only allow re-saving with CSV values
                    if (asset.playImporterId && isDatamartField(this.rule()?.field)) {
                        this.contentLibraryRuleForm.controls.fieldName.reset();
                        this.contentLibraryRuleForm.controls.dataType.reset();
                    }

                    this.subscribeToValidationState(
                        this.contentLibraryRuleForm,
                        'rules',
                        this.#validationService,
                        this.#validationStateService
                    );
                    this.contentLibraryRuleForm.markAllAsTouched();
                });
        });
        this.#contentAssetStore.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#contentAssetStore.resetMessage();
        });
    }

    onComparatorSelectionChange(event: MatSelectChange) {
        const comparator = this.comparators().find(comp => comp.value === event.source.value);
        if (comparator) {
            if (this.nonNegatableComparators.includes(comparator.value)) {
                this.contentLibraryRuleForm.controls.not.reset();
            }
            if (comparator.value === 'blank' || comparator.value === 'isNull') {
                this.contentLibraryRuleForm.controls.value.reset();
                this.contentLibraryRuleForm.controls.value.disable();
            } else {
                this.contentLibraryRuleForm.controls.value.enable();
            }
        }
    }

    multiPickChange(picklistValues: DataMartPicklistValue[]) {
        const values = picklistValues.map(e => e.value);
        if (values.length > 0) {
            this.contentLibraryRuleForm.controls.value.patchValue(JSON.stringify(values));
        } else {
            this.contentLibraryRuleForm.controls.value.patchValue('');
        }
        this.contentLibraryRuleForm.controls.value.markAllAsDirty();
        this.contentLibraryRuleForm.controls.value.markAllAsTouched();
        this.contentLibraryRuleForm.markAllAsDirty();
    }

    async onSave(fromTemplate = false) {
        const ruleToSave = this.buildRule();

        const contentAsset = {
            ...this.#contentAssetStore.asset(),
            updatedBy: this.loggedInUserId(),
            valid: this.#validationStateService.invalidSections().length === 0,
        };
        if (contentAsset) {
            if (!contentAsset.rules) {
                contentAsset.rules = [];
            }
            const index = contentAsset.rules.findIndex(rule => rule.id === ruleToSave.id);
            if (index !== -1) {
                contentAsset.rules[index] = ruleToSave;
            } else {
                contentAsset.rules = [...contentAsset.rules, ruleToSave];
            }
            if (!(await this.#invalidSaveWarningService.confirmSave('content asset', contentAsset))) {
                return noopFormSaveMutationResult();
            }
            return this.#contentAssetStore.saveAsset({ asset: contentAsset }).then(result => {
                if (fromTemplate && wasSaveSuccessful(result)) {
                    this.contentLibraryRuleForm.markAsPristine();
                    this.navigateToRulesList();
                }
                return result;
            });
        } else {
            return noopFormSaveMutationResult();
        }
    }
    onCancel() {
        this.navigateToRulesList();
    }

    subscribeToValidationState(
        form: FormGroup<ContentLibraryRuleForm>,
        sectionName: ContentLibrarySectionName,
        validationService: ValidationService<ContentLibrarySectionName, ContentAsset, ContentLibrarySectionFormMap>,
        validationStateService: ValidationStateService<ContentLibrarySectionName>
    ) {
        return this.#syncValidationStateService.subToValidationState(
            form,
            sectionName,
            validationService,
            validationStateService
        );
    }

    private navigateToRulesList() {
        const contentAsset = this.#contentAssetStore.asset();
        if (contentAsset) {
            this.#router.navigate([`content-library/${contentAsset.id}/rules`]);
        }
    }

    private buildRule(): ContentAssetRule {
        const ruleFormValue = this.contentLibraryRuleForm.getRawValue();
        const { getUsedFieldMerges, getField, getUsedImporterFileHeaders } = this.getBuildRuleHelpers();
        const normalizedTemplate = this.#froalaImagesService.normalizeHtmlForSave(
            ruleFormValue.talkingPointsTemplate ?? ''
        );

        return {
            id: this.rule()?.id ?? uuid.v4().substring(0, 8),
            name: ruleFormValue.name,
            field: getField(),
            not: ruleFormValue.not,
            comparator: ruleFormValue.comparator,
            value: ruleFormValue.value,
            talkingPointsTemplate: normalizedTemplate,
            dataMartFieldMerges: getUsedFieldMerges(),
            importerFileHeaders: getUsedImporterFileHeaders(),
            images: Array.from(this.#froalaImagesService.getUsedImages(document)),
        };
    }

    private getBuildRuleHelpers() {
        const usedImporterFileHeaders = () => {
            const usedFileHeaders: string[] = [];
            const fieldMergeSpans = document
                .querySelector('.fr-element')
                ?.querySelectorAll('span.file-header-field-merge');
            fieldMergeSpans?.forEach(span => {
                if (!usedFileHeaders.includes(span.id)) {
                    usedFileHeaders.push(span.id);
                }
            });
            return usedFileHeaders;
        };

        const dataField = () => {
            const ruleFormValue = this.contentLibraryRuleForm.getRawValue();

            let field;
            if (this.type() === 'datamart') {
                field = this.fields()?.find(fld => fld.name === ruleFormValue.fieldName);
                if (field) {
                    field.tableName =
                        this.#globalLoggedInUserStore.selectedDivisionConfig()?.config?.mapping?.dataMartTable;
                }
            } else {
                field = { name: ruleFormValue.fieldName, dataType: ruleFormValue.dataType } as PlayImporterCSVField;
            }

            return field;
        };

        const usedFieldMerges = () => {
            const usedTfsFieldMerges: DataMartMergeField[] = [];
            const fieldMergeSpans = document.querySelector('.fr-element')?.querySelectorAll('span.tfs-field-merge');
            const fieldMergeDivs = document.querySelector('.fr-element')?.querySelectorAll('div.tfs-field-merge');

            fieldMergeSpans?.forEach(span => {
                const options = span.getAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS) ?? '';
                const fieldMerge: DataMartMergeField = JSON.parse(options);
                const exists = usedTfsFieldMerges.find(
                    existingFieldMerge => existingFieldMerge.name === fieldMerge.name
                );
                if (!exists) {
                    usedTfsFieldMerges.push(fieldMerge);
                }
            });
            fieldMergeDivs?.forEach(div => {
                const options = div.getAttribute(FieldMergeConstants.DATA_CONTENT_OPTIONS) ?? '';
                const fieldMerge: DataMartMergeField = JSON.parse(options);
                const exists = usedTfsFieldMerges.find(
                    existingFieldMerge => existingFieldMerge.name === fieldMerge.name
                );
                if (!exists) {
                    usedTfsFieldMerges.push(fieldMerge);
                }
            });
            return usedTfsFieldMerges;
        };

        return {
            getUsedFieldMerges: usedFieldMerges,
            getField: dataField,
            getUsedImporterFileHeaders: usedImporterFileHeaders,
        };
    }

    public onFieldSelectionChange() {
        if (this.type() === 'csv') {
            this.contentLibraryRuleForm.patchValue({
                dataType: '',
                comparator: null,
                value: null,
            });
        } else if (this.type() === 'datamart') {
            this.contentLibraryRuleForm.patchValue({
                comparator: null,
                value: null,
            });
        }
    }
}
